/**
 * Per-record-type "required field" flags for the EventModal.
 *
 * Centralizes which fields must be highlighted (and which gate the Create/Update
 * button) for each record type, mirroring the logic in saveValidation so the red
 * highlighting clears exactly when a field stops blocking the save.
 */

/** Inputs needed to derive the required-field flags. */
export interface RequiredFlagsInput {
  isReadOnly: boolean;
  mode: 'view' | 'edit' | 'create';
  recordType: string;
  isScheduled: boolean;
  isDeferred: boolean;
  /** When true (status being set to Deleted), no field is required. */
  isDeleted: boolean;
}

/** The set of required-field flags consumed by the modal sections. */
export interface RequiredFlags {
  tailRequired: boolean;
  defectsRequired: boolean;
  notesRequired: boolean;
  ataRequired: boolean;
  nextAdviseRequired: boolean;
  ertsRequired: boolean;
  scheduleDateRequired: boolean;
  stationRequired: boolean;
  deferredFieldsRequired: boolean;
  historyRequired: boolean;
}

/**
 * Derives the required-field flags for the current record type and mode.
 */
export function computeRequiredFlags(input: RequiredFlagsInput): RequiredFlags {
  const { isReadOnly, mode, recordType, isScheduled, isDeferred, isDeleted } = input;
  const requiresFullDetail =
    mode === 'create' && (recordType === 'Unscheduled' || recordType === 'Planned');
  // A record being deleted has no required fields — clear every highlight.
  const editable = !isReadOnly && !isDeleted;
  return {
    tailRequired: editable,
    defectsRequired: editable && (requiresFullDetail || isDeferred || isScheduled),
    notesRequired: editable && (requiresFullDetail || isDeferred || isScheduled),
    ataRequired: editable && (requiresFullDetail || isScheduled || isDeferred),
    nextAdviseRequired: editable && (requiresFullDetail || isScheduled),
    ertsRequired: editable && isScheduled,
    // Time Declared (shown as "Schedule Date" for Scheduled) is required for
    // every record type.
    scheduleDateRequired: editable,
    stationRequired: editable && isScheduled,
    deferredFieldsRequired: editable && isDeferred,
    historyRequired: editable && !isScheduled,
  };
}
